/**
 * Etsy Variation URL Builder
 * 
 * Generates direct links to specific size/color combinations on Etsy
 * Uses variation data imported from the EtsyCrawler
 */

/**
 * Generate a direct Etsy URL for a specific size and color variation
 * 
 * @param {string} baseUrl - The base Etsy listing URL (e.g., "https://www.etsy.com/listing/846621034/...")
 * @param {Array} sizeVariations - Array of size variation objects with {id, name, value}
 * @param {Array} colorVariations - Array of color variation objects with {id, name, value}
 * @param {string} selectedSize - The size value to find (e.g., "MICRO 4.4w x 2.1h inches ($5.99)")
 * @param {string} selectedColor - The color value to find (e.g., "Black")
 * @returns {string|null} - Direct Etsy URL with variation parameters, or null if not found
 */
export function buildEtsyVariationUrl(baseUrl, sizeVariations, colorVariations, selectedSize, selectedColor) {
  if (!baseUrl) return null;
  
  // Parse variation data if it's a JSON string
  const sizes = typeof sizeVariations === 'string' ? JSON.parse(sizeVariations) : sizeVariations;
  const colors = typeof colorVariations === 'string' ? JSON.parse(colorVariations) : colorVariations;
  
  // If no variations, return base URL
  if (!sizes || !colors || (sizes.length === 0 && colors.length === 0)) {
    return baseUrl.split('?')[0]; // Remove any existing params
  }
  
  let sizeId = null;
  let colorId = null;
  
  // Find size variation ID
  if (sizes && sizes.length > 0 && selectedSize) {
    const sizeVar = sizes.find(s => {
      // Try exact match first
      if (s.value === selectedSize) return true;
      
      // Try matching just the size name (e.g., "MICRO" in "MICRO 4.4w x 2.1h inches ($5.99)")
      const sizeName = selectedSize.split(' ')[0].toUpperCase();
      if (s.value.toUpperCase().includes(sizeName)) return true;
      
      return false;
    });
    
    if (sizeVar) {
      sizeId = sizeVar.id;
    } else {
      // If no match found, use first size as fallback
      sizeId = sizes[0].id;
    }
  }
  
  // Find color variation ID
  if (colors && colors.length > 0 && selectedColor) {
    const selectedColorLower = selectedColor.toLowerCase().trim();
    
    // ONLY use exact match - no partial matching to prevent "Brown" matching "Nut Brown"
    const colorVar = colors.find(c => {
      const colorValueLower = c.value.toLowerCase().trim();
      return colorValueLower === selectedColorLower;
    });
    
    if (colorVar) {
      colorId = colorVar.id;
    } else {
      console.warn(`⚠️ No exact color match found for "${selectedColor}". Available colors:`, colors.map(c => c.value));
      // If no match found, use first color as fallback
      colorId = colors[0].id;
    }
  }
  
  // Build URL with variation parameters
  const cleanUrl = baseUrl.split('?')[0]; // Remove any existing params
  
  if (sizeId && colorId) {
    return `${cleanUrl}?variation0=${sizeId}&variation1=${colorId}`;
  } else if (sizeId) {
    return `${cleanUrl}?variation0=${sizeId}`;
  } else if (colorId) {
    return `${cleanUrl}?variation1=${colorId}`;
  }
  
  return cleanUrl;
}

/**
 * Get available sizes for a listing
 * 
 * @param {Array|string} sizeVariations - Size variations (array or JSON string)
 * @returns {Array} - Array of size names
 */
export function getAvailableSizes(sizeVariations) {
  if (!sizeVariations) return [];
  
  const sizes = typeof sizeVariations === 'string' ? JSON.parse(sizeVariations) : sizeVariations;
  return sizes.map(s => s.value);
}

/**
 * Get available colors for a listing
 * 
 * @param {Array|string} colorVariations - Color variations (array or JSON string)
 * @returns {Array} - Array of color names
 */
export function getAvailableColors(colorVariations) {
  if (!colorVariations) return [];
  
  const colors = typeof colorVariations === 'string' ? JSON.parse(colorVariations) : colorVariations;
  return colors.map(c => c.value);
}

/**
 * Extract just the size name from a full size string
 * e.g., "MICRO 4.4w x 2.1h inches ($5.99)" -> "MICRO"
 * 
 * @param {string} sizeString - Full size string
 * @returns {string} - Size name only
 */
export function extractSizeName(sizeString) {
  if (!sizeString) return '';
  return sizeString.split(' ')[0].toUpperCase();
}

